import { ColDef } from 'ag-grid-community';
import { DATE_FORMATS, parseLocalizedNumber } from '@callmart/types';
import { getUpcomingMonths } from '../../utils/dateUtils';
import { currencyValueFormatter } from '../../utils/numberUtils';
import { MAX_SCENARIO_MONTHS, monthSpan } from './scenarioMeta';
import dayjs from '@utils/dayjs';

/** CALL-745 follow-up: budget month grids show a scrolling window of this many months. */
export const BUDGET_WINDOW_SIZE = 12;

// CALL-796: the budget formatters became the app-wide ones and moved to utils/numberUtils.ts,
// beside the parser they have to agree with. Re-exported here so the budget grids keep their
// existing import path — this file stays the budget entry point for both halves.
export {
    currencyFormatter,
    priceFormatter,
    integerFormatter,
    decimalFormatter,
    formatCurrency,
    // CALL-813: whole euros for the Overview's read-only TOTALS; optional cents for the hand-typed
    // Ist-Budget row, which is entered in whole thousands but must still show a cent if one was typed.
    formatWholeEuro,
    // The figure formatWholeEuro renders — for the Delta colour rules, so they cannot disagree with it.
    wholeEuroValue,
    wholeEuroValueFormatter,
    formatEuroOptionalCents,
    formatPercent,
    formatDecimal,
} from '../../utils/numberUtils';

/** Strip everything except digits, commas, dots, minus — parseLocalizedNumber doesn't handle currency symbols */
export const stripCurrencySymbols = (value: unknown): string => String(value).replace(/[^0-9.,-]/g, '');

// CALL-744 #12: every budget grid renders with hard-coded de-DE formatters, so cell input must be
// parsed as German regardless of the browser locale. The default numberUtils wrapper passes
// navigator.language, which resolves an ambiguous thousands value like "1.000" as 1.0 on a non-German
// browser — silently storing 1 instead of 1000. Pin the locale to 'de' to match what the grids show.
export const parseBudgetNumber = (value: string | number | null | undefined): number | null =>
    parseLocalizedNumber(value, 'de');

/** ag-grid valueParser for every editable budget/scenario number cell: strip the unit the formatter
 *  added (€, %) and parse the rest as German. Returns null for a cleared cell. */
export const parseBudgetCell = (value: unknown): number | null => parseBudgetNumber(stripCurrencySymbols(value));

/** Scenario dates are plain DATEONLY strings ('YYYY-MM-DD') — format them as-is, WITHOUT the
 *  UTC→Europe/Berlin conversion utils/dayjs.formatDateOnly applies, which would shift a date-only
 *  value across a day boundary. */
export const formatBudgetDate = (value?: unknown): string =>
    value ? dayjs(value as string).format(DATE_FORMATS.DATE) : '';

/** Generate 12 month keys (YYYY-MM-DD) for a given year, as a default set */
export const generateDefaultMonths = (year = new Date().getFullYear()): string[] =>
    Array.from({ length: 12 }, (_, m) => dayjs().year(year).month(m).startOf('month').format('YYYY-MM-DD'));

/** CALL-749: contiguous month keys (YYYY-MM-DD) from the earliest to the latest data month,
 *  inclusive. This does NOT pad out to whole calendar years, so entering e.g. a Dec 2025 budget
 *  (with prices for 2026) shows Dez 25 … Dez 26 — not all of 2025.
 *  Falls back to the current-year defaults when there is no data yet. */
export const getContiguousMonths = (dataMonths: string[]): string[] => {
    const sorted = Array.from(new Set(dataMonths.filter(Boolean))).sort();
    if (!sorted.length) return generateDefaultMonths();

    const end = dayjs(sorted[sorted.length - 1], DATE_FORMATS.ISO_DATE).startOf('month');
    const months: string[] = [];
    let cursor = dayjs(sorted[0], DATE_FORMATS.ISO_DATE).startOf('month');
    while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
        months.push(cursor.format('YYYY-MM-DD'));
        cursor = cursor.add(1, 'month');
    }
    return months;
};

/** Month keys (YYYY-MM-DD) for a scrolling window of `size` consecutive months from windowStart.
 *  The window is data-independent — it always returns `size` columns, so months with no data yet
 *  (a new/previous year) are editable without an "add" button. */
export const getWindowMonths = (windowStart: string, size = BUDGET_WINDOW_SIZE): string[] =>
    getUpcomingMonths(windowStart, size).map((m) => m.format('YYYY-MM-DD'));

/** CALL-781: the view's months from `currentMonth` onward — PRESENTATION ONLY.
 *  Feed this to generateMonthColumns and nothing else: callers must keep the full month list for
 *  anything that computes (row completeness, extrapolation, the pinned total), or a display toggle
 *  starts moving figures. Deliberately NOT folded into getWindowMonths — half of that helper's callers
 *  are the Prices & ABT grids, which this filter must not touch.
 *
 *  CALL-815: "the view's months" is the 12-month window OR, when a `Datum von`/`bis` range is set, the
 *  months of that range (see useOverviewMonths). The pinned total follows THAT list — the range is a
 *  lens the user chose the figures for, exactly as the year window has been since CALL-772 — while
 *  this filter still never moves a figure. The rule above is unchanged: compute from the list, never
 *  from what this returns.
 *
 *  The cutoff is INJECTED, not read off the clock here (see useCurrentAppMonth). Reading it internally
 *  left the callers' useMemo with no time-dependent dep, so an Overview left open across midnight on the
 *  1st went on showing the month that had just ended until something else invalidated the memo. Passing
 *  it in also keeps this pure.
 *
 *  A plain string compare is exact here: every month key is a zero-padded first-of-month (MONTH_KEY). */
export const getVisibleMonths = (months: string[], hidePast: boolean, currentMonth: string): string[] =>
    hidePast ? months.filter((month) => month >= currentMonth) : months;

/** Contiguous first-of-month keys from startDate to endDate — a scenario's own window, independent of
 *  the shared Budget month window. Matches the backend's generateMonthsBetween. Fed from the draft
 *  period rather than the saved one, so editing Start/Ende shows the new columns immediately. */
export const scenarioMonths = (startDate?: string, endDate?: string): string[] => {
    if (!startDate || !endDate) return [];
    const end = dayjs(endDate).startOf('month');
    const months: string[] = [];
    let cursor = dayjs(startDate).startOf('month');
    while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
        months.push(cursor.format('YYYY-MM-DD'));
        cursor = cursor.add(1, 'month');
    }
    return months;
};

/** CALL-815: whether the Overview's optional `Datum von` / `Datum bis` filter is a usable period, and
 *  therefore APPLIED. Both ends have to be set, in order, and within the same span cap the scenario
 *  period uses — a half-typed or reversed range leaves the 12-month window exactly as it was and is
 *  reported on the field it belongs to (BudgetMonthRangeFilter), instead of emptying every grid on the
 *  page while someone is still picking the second date. */
export const isBudgetRangeActive = (from?: string, to?: string): boolean =>
    Boolean(from && to && to >= from && monthSpan(from, to) <= MAX_SCENARIO_MONTHS);

/** An active range as a caption — "Jan. 26 – März 27". Used by the pinned total's header and by the
 *  (read-only) window nav, so the two name the period identically.
 *
 *  The month names follow the GLOBAL dayjs locale, which i18n.js re-points at the UI language on every
 *  `languageChanged` — so an English session reads "Jan 26 – Mar 27". Deliberately NOT pinned to 'de' like
 *  the range pickers: this caption sits directly above the month COLUMN HEADERS, which generateMonthColumns
 *  formats with the same `MMM YY` on the same global locale. Pinning the caption alone would print
 *  "Jan. 26 – März 27" over columns reading "Jan 26 … Dec 26". */
export const formatMonthRange = (from: string, to: string): string =>
    `${dayjs(from, DATE_FORMATS.ISO_DATE).format('MMM YY')} – ${dayjs(to, DATE_FORMATS.ISO_DATE).format('MMM YY')}`;

/** Every scenario grid, input and impact alike. Two classes because only the second one's rules are
 *  scenario-specific: `budget-grid` carries the cell-editor and auto-height rules shared with the other
 *  budget tables, `scenario-grid` the header truncation and the pinned-right scroll boundary (CALL-784).
 *  Scoped rather than global because the other budget grids have wide columns and WANT wrapping headers. */
export const SCENARIO_GRID_CLASS = 'budget-grid scenario-grid';

/** Row height of every scenario grid — inputs, impact and the overview alike (CALL-783, CALL-794).
 *
 *  Taller than the shared budget-grid row (the AgGrid wrapper's `rowHeight: 32`), because the board wraps a
 *  long caption over TWO lines — "Blau Care Postpaid - Inbound Voice - Alle" — where these grids truncated it
 *  at one. Two 13px lines at a 1.25 line height are 32.5px, so 52 holds them with the theme's vertical
 *  padding and still fits the 30px action icons the overview carries; a third line (48.75px) no longer does,
 *  which is why the identifying columns are sized so no real label needs one — see
 *  SCENARIO_ROW_LABEL_COL_WIDTH. It was 64 (2 × 32) until CALL-794: that left an empty band above and below
 *  every two-line row, and a visibly airier table than the board it reproduces.
 *
 *  ONE number for every scenario grid, for the same reason as the header height: the input⇄impact toggle
 *  swaps two tables in place, and a different row height on either side would move everything below the grid
 *  on every click. The overview shares it so the module reads as one table style rather than two.
 *
 *  One thing follows this constant and is NOT derived from it, because ag-grid does not expose it to CSS: the
 *  `.budget-grid.scenario-grid .ag-cell-inline-editing` height in App.css, which has to stay row + 2px of
 *  border (the theme sizes the editor from `--ag-row-height`, which it leaves at 48px). Change this and that
 *  literal has to change with it — that coupling has bitten this file three times. */
export const SCENARIO_ROW_HEIGHT = 52;

/** Caps a wrapped cell at the two lines SCENARIO_ROW_HEIGHT fits, ending in an ellipsis (CALL-800).
 *
 *  The row clips a third line on its own — but silently and mid-glyph, so a long description showed two
 *  full lines with a band of half-drawn letters under them, which reads as a broken table rather than as
 *  "there is more, hover for it".
 *
 *  Belongs on an element INSIDE the cell, never on the cell: `-webkit-line-clamp` works by giving its box
 *  a two-line height, and ag-grid sets every cell's height from the row — with a height already set the
 *  clamp does nothing (measured on a five-line value, which drew all five). ag-grid writes a plain value
 *  straight into the cell, so a column using this needs a cellRenderer to supply that element. */
export const SCENARIO_CLAMP_TWO_LINES = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
};

/** Header height of every scenario grid (CALL-784).
 *
 *  The material theme's default is 56px, which against a 32px row made the header the tallest thing in the
 *  table — the board's proportions are the inverse (header ~30, rows ~35). 36 is a single line of 13px
 *  header text plus breathing room, so it still reads as a header without a second line. It has to be ONE
 *  number: the input⇄impact toggle swaps two tables in place, and a different header height on either side
 *  moves everything below the grid on every click. Headers no longer wrap (see the .scenario-grid rules in
 *  App.css), so nothing needs the extra line. */
export const SCENARIO_HEADER_HEIGHT = 36;

/** The pinned identifier column of EVERY scenario input and impact grid (CALL-783).
 *
 *  One constant, not a per-grid number: the input⇄impact toggle swaps two same-shaped tables in place, so
 *  a different width on either side would shift the whole page sideways on every click. Pinned columns are
 *  excluded from ag-grid's flex distribution, so every pixel here is one the month block cannot use — which
 *  is why it went 220 → 180 (CALL-784) → 160 (CALL-783).
 *
 *  160 does not hold the longest label on one line and is not meant to: the rows are double height and these
 *  cells WRAP (see SCENARIO_ROW_HEIGHT), the way the board's own captions do. What it IS sized for is the
 *  rule that no label may break mid-word — see WRAPPED LABEL WIDTHS below. */
export const SCENARIO_ENTITY_COL_MIN_WIDTH = 160;

/** The non-pinned LABEL columns of the row input grids — Verschiebung's Von and Nach (CALL-783).
 *
 *  ── WRAPPED LABEL WIDTHS ────────────────────────────────────────────────────────────────────────────
 *  Both label widths are set by ONE rule, not by how many month columns are left over: a label may wrap at
 *  its SPACES but must never break mid-word. So each column has to fit its longest single WORD on one line,
 *  measured at the 13px cell font with the theme's 3px of cell padding a side:
 *
 *    partner–location   "Teleperformance" 103px   → 115 gives 109px of text area  ✓
 *    working unit       "Technikerkoordination" 134px, "Terminvereinbarung" 121px → 160 gives 154px  ✓
 *
 *  A first pass at 130/82 was sized off the arithmetic minimum instead, and split "Teleperfor|mance" —
 *  exactly the mid-word break CALL-784 removed from the headers, reintroduced one layer down in the cells.
 *  App.css resets ag-grid's own `word-break: break-word` on these cells so a word that STILL does not fit
 *  overflows and clips (with its tooltip) rather than splitting.
 *
 *  Two-line capacity at these widths, measured: every real label lands in two lines, including the board's
 *  own longest — "Blau Care Postpaid - Inbound Voice - Alle" (250px on one line) breaks after "Postpaid -".
 *
 *  Used by the shift IMPACT grid too, for the same Von/Nach columns (CALL-783): its rows carry the identity
 *  split into parts, so it renders the same three columns at the same three widths rather than the composite
 *  "unit · Von → Nach" it used to cram into the pinned column — where it wrapped to four lines and clipped.
 *  Identical widths on both sides are what keeps the input⇄impact toggle from shifting the table sideways.
 *
 *  The cost is paid out of the month block, deliberately: legibility of the identifying columns outranks the
 *  number of months on screen. Verschiebung shows three of them at 1400px — see ScenarioShiftGrid. */
export const SCENARIO_ROW_LABEL_COL_WIDTH = 115;

/** The identifier column of the three grids that declare it as a ColDef directly (the row grids build
 *  theirs through ScenarioRowColumn). Spelled out because `minWidth` ALONE is not enough: a pinned column
 *  is excluded from ag-grid's flex distribution, so it falls back to `width` — whose default is 200. At the
 *  old minWidth of 220 that default was invisible; at 180 it made every impact grid render 200 against the
 *  input grid's 180, i.e. exactly the 20px sideways shift on every input⇄impact toggle that one shared
 *  constant exists to prevent (CALL-784). */
export const scenarioEntityColumn = (headerName: string): ColDef => ({
    headerName,
    headerTooltip: headerName,
    width: SCENARIO_ENTITY_COL_MIN_WIDTH,
    minWidth: SCENARIO_ENTITY_COL_MIN_WIDTH,
    flex: 0,
    pinned: 'left',
    cellClass: 'notranslate',
    // CALL-783: the label WRAPS over the two lines SCENARIO_ROW_HEIGHT provides instead of truncating at
    // one, the way the board's own captions read. `autoHeight` is deliberately NOT used with it: it would
    // size each row to its own content, and rows of differing heights are what the fixed height (and the
    // inline-editor height keyed to it) exist to prevent. App.css also resets ag-grid's `word-break:
    // break-word` here, so a label wraps at its spaces and never mid-word.
    wrapText: true,
    // A wrapped label still clips at the second line, so the full name has to be one hover away. The
    // entity columns hold plain strings, hence the raw value.
    tooltipValueGetter: (params) => String(params.value ?? ''),
});

/** The month columns of every scenario IMPACT grid, plus the two input grids whose cells hold a currency
 *  amount (budget adjustment, minute price), as a `width` (CALL-783).
 *
 *  generateMonthColumns emits `minWidth: 110` with no `width`, and it is shared by every budget grid, so it
 *  cannot be changed here. On a flex:0 column ag-grid ignores minWidth and takes `width`, whose DEFAULT is
 *  200 — which is why the month block rendered nearly twice as wide as intended. Each scenario grid passes
 *  this through its own overrides object instead. 110 is what a signed six-figure euro cell needs
 *  ("-110.760,00 €" measures 84px, plus the cell's own padding and the sort/filter icon slot). */
export const SCENARIO_MONTH_COL_WIDTH = 110;

/** The month columns of the three ROW input grids — Verschiebung, GA/CB/VVL, Kontakte (CALL-784).
 *
 *  Narrower than the impact width above because these cells never hold a currency total: a shift ramp is
 *  "100 %" (38px), a driver delta or a contact delta a signed count ("-8.000", 42px). They pay for it: those
 *  three grids carry six to eleven leading columns in front of the months, and at 110 the month block began
 *  past the right edge of a 1400px viewport — on the very screens whose subject IS the monthly ramp
 *  (CALL-784). Their impact view keeps 110, so the toggle does move the month block sideways; that is the
 *  accepted trade for having the ramp on screen at all, and the two views already differ in leading columns.
 *  Also the board's own month width (~70px). */
export const SCENARIO_ROW_MONTH_COL_WIDTH = 80;

/** colId of the trailing filler column — the tests address it by this rather than by position. */
export const SCENARIO_FILLER_COL_ID = 'filler';

/** A trailing, headerless column that soaks up whatever width the grid has left over (CALL-784).
 *
 *  Every scenario column is flex:0 (they have to be — see SCENARIO_MONTH_COL_WIDTH), so a short period left
 *  up to half the table as bare background inside its own border: 770px of columns in a 1487px shell on a
 *  five-month minute-price scenario. The first attempt put `flex: 1` on the LAST MONTH instead, which fills
 *  the shell just as well and needs no extra column — but it detached the value from its caption: a 700px-wide
 *  "Dez. 26" on the one-row budget-adjustment grid printed its right-aligned euro figure 650px away from its
 *  own header. A filler keeps every data column at its declared width.
 *
 *  NOT on every scenario grid, though — only on the ones whose last column really is their last DATA column.
 *  Where a column is pinned right (the row grids' trash, the impact grid's Gesamt) ag-grid already draws that
 *  section flush against the shell's right border, so a filler in front of it only stretches a bordered empty
 *  box across the gap: 427px of it between "Dez. 26" and Gesamt on a three-month impact table, which reads as
 *  a broken table rather than a wide one. Those grids leave the gap as plain background instead.
 *
 *  `minWidth: 0` is what makes it safe on a grid that already overflows: ag-grid gives a flex column its
 *  minWidth when there is no space left over, so the default (or any positive floor) would be a dead column
 *  that forces even more horizontal scrolling. At 0 it disappears — but only from ag-grid's arithmetic: the
 *  cell still MEASURED 8px, because the theme's `padding: 0 4px` is a floor under a border-box width of 0.
 *  The App.css `.scenario-grid [col-id='filler']` rule is what takes those 8px off, and it also makes the
 *  cell inert: `suppressNavigable` keeps the keyboard out, but a CLICK still painted ag-cell-focus — a 1px
 *  indigo border around a 557px empty box, which reads exactly like an editable field. */
export const scenarioFillerColumn = (): ColDef => ({
    colId: SCENARIO_FILLER_COL_ID,
    headerName: '',
    flex: 1,
    minWidth: 0,
    editable: false,
    sortable: false,
    filter: false,
    resizable: false,
    suppressNavigable: true,
});

/** A month column's `field` — the key generateMonthColumns puts there. Every budget grid tests a
 *  committed cell's field against this before treating it as a monthly value and saving it, so it has
 *  to be ONE definition: four copies meant a change to the month-key format (or tightening it to
 *  `-01`, as the backend's monthKeySchema has it) could reach three grids and silently leave the
 *  fourth either not autosaving or posting non-month columns. */
export const MONTH_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** Generate AG Grid column defs for a list of month keys, with optional per-column overrides */
export const generateMonthColumns = (months: string[], overrides?: Partial<ColDef>): ColDef[] =>
    months.map((monthKey, index) => {
        const monthDate = dayjs(monthKey, DATE_FORMATS.ISO_DATE);
        return {
            // CALL-745 perf (F6): a STABLE POSITIONAL colId. ag-grid keys columns by colId, so when
            // the scrolling window shifts, m0..m{n} stay constant and ag-grid updates the existing
            // columns in place (field/header/values) instead of destroying and recreating the whole
            // month-column set — turning a per-shift column rebuild into a cheap cell+header refresh.
            // Save handlers key off `field` (the real month), so they're unaffected.
            colId: `m${index}`,
            headerName: monthDate.format('MMM YY'),
            field: monthKey,
            minWidth: 110,
            // CALL-745 perf: month columns are fixed-width and scroll horizontally, so they must
            // NOT participate in the global defaultColDef flex:1 width distribution — flex forces a
            // full-width reflow across all columns on every window rebuild.
            flex: 0,
            editable: false,
            cellStyle: { textAlign: 'right' },
            valueFormatter: currencyValueFormatter,
            ...overrides,
        };
    });
